import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { LanguageOptionType } from '@core/types/layout';
import { TranslateService } from '@ngx-translate/core';
import { French } from 'flatpickr/dist/l10n/fr.js';
import { Dutch } from 'flatpickr/dist/l10n/nl.js';
import { German } from 'flatpickr/dist/l10n/de.js';
import DefaultLocale from 'flatpickr/dist/l10n/default.js';
import { NgSelectConfig } from '@ng-select/ng-select';
import { FormBuilder, ValidatorFn } from '@angular/forms';

const STORAGE_KEY = '__GOHY__LANG__';

const availableLanguages: LanguageOptionType[] = [
    {code: 'fr', name: 'French', nativeName: 'Français', flag: 'img/flags/fr.svg', flatpickr: French },
    {code: 'nl', name: 'Dutch', nativeName: 'Nederlands', flag: 'img/flags/nl.svg', flatpickr: Dutch },
    {code: 'en', name: 'English', nativeName: 'English', flag: 'img/flags/gb.svg', flatpickr: DefaultLocale },
    {code: 'de', name: 'German', nativeName: 'Deutsch', flag: 'img/flags/de.svg', flatpickr: German },
];

@Injectable({providedIn: 'root'})
export class LanguageService {

    translateService: TranslateService;

    private currentLangSubject = new BehaviorSubject<LanguageOptionType>(availableLanguages[0]);
    currentLang$ = this.currentLangSubject.asObservable();

    get currentLang() {
        return this.currentLangSubject.value;
    }

    constructor(
        private fb: FormBuilder,
        private ngSelectConfig: NgSelectConfig,
    ) {
        this.translateService = new TranslateService();

        this.translateService.onLangChange.subscribe(() => {
            this.ngSelectConfig.addTagText = this.translateService.instant('modules.ng-select.add_tag');
            this.ngSelectConfig.loadingText = this.translateService.instant('modules.ng-select.loading');
            this.ngSelectConfig.notFoundText = this.translateService.instant('modules.ng-select.not_found');
            this.ngSelectConfig.typeToSearchText = this.translateService.instant('modules.ng-select.type_to_search');
            this.ngSelectConfig.clearAllText = this.translateService.instant('modules.ng-select.clear_all');
        });

        this.initLanguage();
    }

    getLanguages(): LanguageOptionType[] {
        return availableLanguages;
    }

    setLanguage(code: string) {
        const lang = this.findLanguage(code);

        if (lang) {
            this.currentLangSubject.next(lang);
            this.translateService.use(code);
            localStorage.setItem(STORAGE_KEY, code);
        }
    }

    findLanguage(code: string) {
        return availableLanguages.find(l => l.code === code);
    }

    private initLanguage() {
        let saved = localStorage.getItem(STORAGE_KEY);

        if (!saved) {
            const browserLang = this.translateService.getBrowserLang();

            if (browserLang) {
                const lang = this.findLanguage(browserLang);
                if (lang) {
                    saved = lang.code;
                }
            }
        }

        this.setLanguage(saved || 'fr');
    }

    formGroup(validators: ValidatorFn|ValidatorFn[] = []) {
        const group = this.fb.group({});

        for (let language of this.getLanguages()) {
            group.addControl(language.code, this.fb.control(null, language.code === this.currentLang.code ? validators : []));
        }

        return group;
    }

    hasTradKey(key: string) {
        return this.translateService.instant(key) !== key;
    }
}
